'use client';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CardMedia from '@mui/material/CardMedia';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { relativePath } from '@/functions/redirectToRelativePath';
import { shouldOpenOtherTab } from '@/functions/shouldOpenOtherTab';

type Props = {
  customerSetting: CustomerSetting;
};

export const Header: React.FC<Props> = ({ customerSetting }) => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawerContent = (dense: boolean) => (
    <>
      {customerSetting.header.menuItems.map((item, i) => (
        <ListItem
          key={`header-menu-item-${i}`}
          sx={{
            p: 0,
            width: { md: 'max-content' }
          }}
        >
          <ListItemButton
            dense={dense}
            sx={{ px: 1 }}
            LinkComponent="a"
            href={relativePath(item.url, customerSetting.domain)}
            target={shouldOpenOtherTab(item.url) ? '_blank' : undefined}
            onClick={() => setDrawerOpen(false)}
          >
            <ListItemText primary={item.title} />
          </ListItemButton>
        </ListItem>
      ))}
    </>
  );

  return (
    <>
      <AppBar
        component="header"
        position="static"
        sx={{ backgroundColor: 'white', color: 'inherit' }}
      >
        <Toolbar sx={{ p: { xs: 0 } }}>
          <Container
            maxWidth="lg"
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 2,
                rowGap: 1
              }}
            >
              <ButtonBase
                LinkComponent="a"
                href={relativePath('/', customerSetting.domain)}
              >
                <CardMedia
                  component="img"
                  sx={{
                    width: {
                      md: customerSetting.header.imageWidth,
                      sm: 0.6 * customerSetting.header.imageWidth,
                      xs: 0.6 * customerSetting.header.imageWidth
                    },
                    height: {
                      md: customerSetting.header.imageHeight,
                      sm: 0.6 * customerSetting.header.imageHeight,
                      xs: 0.6 * customerSetting.header.imageHeight
                    },
                    objectFit: 'contain'
                  }}
                  image={customerSetting.header.imageUrl}
                  alt={customerSetting.siteName}
                  loading="lazy"
                />
              </ButtonBase>
              {customerSetting.header.title && (
                <ButtonBase
                  LinkComponent="a"
                  href={relativePath('/', customerSetting.domain)}
                >
                  <Typography
                    component="h2"
                    variant="h6"
                    sx={{
                      fontSize: {
                        xs: theme.typography.body1.fontSize,
                        sm: theme.typography.h6.fontSize
                      }
                    }}
                  >
                    {customerSetting.header.title}
                  </Typography>
                </ButtonBase>
              )}
            </Box>
            <Box>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'none', md: 'block' }
                }}
              >
                <List sx={{ display: 'flex', flexDirection: 'row' }}>
                  {drawerContent(true)}
                </List>
              </Box>
              <IconButton
                edge="start"
                aria-label="menu"
                color="primary"
                onClick={handleDrawerToggle}
                sx={{ display: { sm: 'block', md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Container>
        </Toolbar>
      </AppBar>
      {/* モバイル表示のドロワー */}
      <Drawer anchor="top" open={drawerOpen} onClose={handleDrawerToggle}>
        <List>{drawerContent(false)}</List>
      </Drawer>
    </>
  );
};
